import {
  memo,
  useCallback,
  useEffect,
  useRef,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Pencil } from 'lucide-react';

import { Tooltip } from '@/components/Tooltip/Tooltip';

import { ContentUnitKebabMenu } from './ContentUnitKebabMenu';
import styles from './EditableUnitFrame.module.css';

interface EditableUnitFrameProps {
  activeEditor?: ReactNode;
  anchorId?: string;
  children: ReactNode;
  disabledReason?: string;
  editMode: boolean;
  isDragOverlay?: boolean;
  isActive?: boolean;
  canMoveDown?: boolean;
  canMoveUp?: boolean;
  isDragDisabled?: boolean;
  isTinted?: boolean;
  onDelete?: () => void;
  onCopy?: () => void;
  onCopyUnit?: (unitId: string) => void;
  onEdit?: () => void;
  onEditUnit?: (unitId: string) => void;
  onMoveDown?: () => void;
  onMoveUnit?: (fromIndex: number, toIndex: number) => void;
  onMoveUp?: () => void;
  onDeleteUnit?: (unitId: string) => void;
  moveIndex?: number;
  unitIndex?: number;
  unitId: string;
}

export const EditableUnitFrame = memo(function EditableUnitFrame({
  activeEditor,
  anchorId,
  children,
  disabledReason,
  editMode,
  canMoveDown = false,
  canMoveUp = false,
  isActive = false,
  isDragDisabled = false,
  isDragOverlay = false,
  isTinted = false,
  onDelete,
  onDeleteUnit,
  onCopy,
  onCopyUnit,
  onEdit,
  onEditUnit,
  onMoveDown,
  onMoveUnit,
  onMoveUp,
  moveIndex,
  unitIndex = 0,
  unitId,
}: EditableUnitFrameProps) {
  if (isDragOverlay) {
    return (
      <div
        className={[styles.frame, styles.dragOverlay].join(' ')}
        data-active-edit="false"
        data-drag-overlay="true"
        data-edit-mode={editMode ? 'true' : 'false'}
        data-editable-unit-id={unitId}
        data-unit-tint={isTinted ? 'blue' : undefined}
        id={editMode ? anchorId : undefined}
      >
        {children}
      </div>
    );
  }

  return (
    <SortableEditableUnitFrame
      activeEditor={activeEditor}
      anchorId={anchorId}
      disabledReason={disabledReason}
      editMode={editMode}
      canMoveDown={canMoveDown}
      canMoveUp={canMoveUp}
      isActive={isActive}
      isDragDisabled={isDragDisabled}
      isTinted={isTinted}
      onDelete={onDelete}
      onDeleteUnit={onDeleteUnit}
      onCopy={onCopy}
      onCopyUnit={onCopyUnit}
      onEdit={onEdit}
      onEditUnit={onEditUnit}
      onMoveDown={onMoveDown}
      onMoveUnit={onMoveUnit}
      onMoveUp={onMoveUp}
      moveIndex={moveIndex}
      unitIndex={unitIndex}
      unitId={unitId}
    >
      {children}
    </SortableEditableUnitFrame>
  );
});

function SortableEditableUnitFrame({
  activeEditor,
  anchorId,
  children,
  disabledReason,
  editMode,
  canMoveDown = false,
  canMoveUp = false,
  isActive = false,
  isDragDisabled = false,
  isTinted = false,
  onDelete,
  onDeleteUnit,
  onCopy,
  onCopyUnit,
  onEdit,
  onEditUnit,
  onMoveDown,
  onMoveUnit,
  onMoveUp,
  moveIndex,
  unitIndex = 0,
  unitId,
}: Omit<EditableUnitFrameProps, 'isDragOverlay'>) {
  const editButtonRef = useRef<HTMLButtonElement | null>(null);
  const wasActiveRef = useRef(isActive);
  const unitLabel = `Content item ${unitIndex + 1}`;
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    disabled: !editMode || isDragDisabled || isActive,
    id: unitId,
  });
  const dragTooltip = isDragDisabled
    ? disabledReason ?? 'Save or cancel current edit first'
    : 'Reorder';
  const sortableStyle: CSSProperties = {
    transform: isDragging ? undefined : CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
  };
  const frameMoveIndex = moveIndex ?? unitIndex;

  const handleDelete = useCallback(() => {
    if (onDeleteUnit) {
      onDeleteUnit(unitId);
      return;
    }

    onDelete?.();
  }, [onDelete, onDeleteUnit, unitId]);

  const handleCopy = useCallback(() => {
    if (onCopyUnit) {
      onCopyUnit(unitId);
      return;
    }

    onCopy?.();
  }, [onCopy, onCopyUnit, unitId]);

  const handleEdit = useCallback(() => {
    if (onEditUnit) {
      onEditUnit(unitId);
      return;
    }

    onEdit?.();
  }, [onEdit, onEditUnit, unitId]);

  const handleMoveDown = useCallback(() => {
    if (onMoveUnit) {
      onMoveUnit(frameMoveIndex, frameMoveIndex + 1);
      return;
    }

    onMoveDown?.();
  }, [frameMoveIndex, onMoveDown, onMoveUnit]);

  const handleMoveUp = useCallback(() => {
    if (onMoveUnit) {
      onMoveUnit(frameMoveIndex, frameMoveIndex - 1);
      return;
    }

    onMoveUp?.();
  }, [frameMoveIndex, onMoveUnit, onMoveUp]);

  useEffect(() => {
    if (wasActiveRef.current && !isActive && editMode) {
      editButtonRef.current?.focus();
    }

    wasActiveRef.current = isActive;
  }, [editMode, isActive]);

  return (
    <div
      className={styles.frame}
      data-active-edit={isActive ? 'true' : 'false'}
      data-dragging={isDragging ? 'true' : 'false'}
      data-edit-mode={editMode ? 'true' : 'false'}
      data-editable-unit-id={unitId}
      data-unit-tint={isTinted ? 'blue' : undefined}
      id={editMode ? anchorId : undefined}
      ref={setNodeRef}
      style={sortableStyle}
    >
      {editMode && !isActive ? (
        <>
          <div className={[styles.cornerAffordance, styles.topLeft].join(' ')}>
            <Tooltip content={dragTooltip}>
              <button
                {...attributes}
                {...listeners}
                aria-label="Reorder"
                className={styles.affordanceButton}
                disabled={isDragDisabled}
                ref={setActivatorNodeRef}
                title={dragTooltip}
                type="button"
              >
                <GripVertical aria-hidden="true" size={16} strokeWidth={2} />
              </button>
            </Tooltip>
          </div>
          <div className={[styles.cornerAffordance, styles.topRight].join(' ')}>
            <Tooltip content="Edit">
              <button
                aria-label="Edit"
                className={styles.affordanceButton}
                onClick={handleEdit}
                ref={editButtonRef}
                title="Edit"
                type="button"
              >
                <Pencil aria-hidden="true" size={15} strokeWidth={2} />
              </button>
            </Tooltip>
            <ContentUnitKebabMenu
              canMoveDown={canMoveDown}
              canMoveUp={canMoveUp}
              disabled={isDragDisabled}
              disabledReason={disabledReason}
              onCopy={onCopy || onCopyUnit ? handleCopy : undefined}
              onDelete={handleDelete}
              onMoveDown={handleMoveDown}
              onMoveUp={handleMoveUp}
              triggerClassName={styles.affordanceButton}
              triggerIcon="horizontal"
              unitLabel={unitLabel}
            />
          </div>
        </>
      ) : null}
      {isActive && activeEditor ? activeEditor : children}
    </div>
  );
}
